import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { BalanceSheetGroupedData } from "~/types/contabilidad";

interface BalanceSheetExportData {
  data: BalanceSheetGroupedData;
  period: string;
  companyName: string;
  templateName: string;
}

function formatNumber(value: number): number {
  return Math.round(value);
}

/**
 * Exports the Balance Sheet (Estado de Situacion Financiera) to Excel
 */
export const exportBalanceSheetToExcel = ({
  data,
  period,
  companyName,
  templateName,
}: BalanceSheetExportData): void => {
  const workbook = XLSX.utils.book_new();

  // Header section data
  const headerData: (string | number)[][] = [
    ["ESTADO DE SITUACION FINANCIERA"],
    [],
    ["Empresa:", companyName],
    ["Periodo:", period],
    ["Plantilla:", templateName],
    ["Fecha de generacion:", dayjs().format("DD/MM/YYYY HH:mm")],
    [],
    ["Descripcion", "Monto"],
  ];

  // Build hierarchical data
  const sheetRows: (string | number)[][] = [];

  for (const clase of data.clases) {
    // CLASE header row (Level 1)
    sheetRows.push([clase.clase.toUpperCase(), ""]);

    for (const subClase of clase.subClases) {
      // SUB_CLASE header row (Level 2)
      sheetRows.push(["  " + subClase.sub_clase, ""]);

      for (const subClase2 of subClase.subClases2) {
        // SUB_CLASE_2 header row (Level 3)
        sheetRows.push(["    " + subClase2.sub_clase_2, ""]);

        for (const grupo of subClase2.grupos) {
          // GRUPO header row (Level 4) - only if named
          if (grupo.grupo_cuenta_name) {
            const grupoLabel = grupo.grupo_cuenta_code
              ? `${grupo.grupo_cuenta_code} - ${grupo.grupo_cuenta_name}`
              : grupo.grupo_cuenta_name;
            sheetRows.push(["      " + grupoLabel, ""]);
          }

          // Individual accounts (Level 5)
          for (const account of grupo.accounts) {
            const indent = grupo.grupo_cuenta_name ? "        " : "      ";
            sheetRows.push([
              `${indent}${account.account_code} - ${account.account_name}`,
              formatNumber(account.saldo_final),
            ]);
          }

          // GRUPO total row (only if named)
          if (grupo.grupo_cuenta_name) {
            sheetRows.push([
              "      Total " + grupo.grupo_cuenta_name,
              formatNumber(grupo.total),
            ]);
          }
        }

        // SUB_CLASE_2 total row
        sheetRows.push([
          "    Total " + subClase2.sub_clase_2,
          formatNumber(subClase2.total),
        ]);
      }

      // SUB_CLASE total row
      sheetRows.push([
        "  Total " + subClase.sub_clase,
        formatNumber(subClase.total),
      ]);
    }

    // CLASE total row
    sheetRows.push([
      "TOTAL " + clase.clase.toUpperCase(),
      formatNumber(clase.total),
    ]);

    // Empty row between classes
    sheetRows.push(["", ""]);
  }

  // Validation row
  sheetRows.push([
    "VALIDACION (Total Activo - Total Pasivo y Patrimonio)",
    formatNumber(data.diferencia),
  ]);

  // Summary section
  sheetRows.push(["", ""]);
  sheetRows.push(["RESUMEN", ""]);
  sheetRows.push(["Total Activo", formatNumber(data.totalActivo)]);
  sheetRows.push([
    "Total Pasivo y Patrimonio",
    formatNumber(data.totalPasivoYPatrimonio),
  ]);
  sheetRows.push(["Diferencia", formatNumber(data.diferencia)]);

  // Combine all data
  const sheetData = [...headerData, ...sheetRows];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 60 }, // Descripcion
    { wch: 20 }, // Monto
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Estado Situacion Financiera"
  );

  // Generate filename
  const filename = `estado_situacion_financiera_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
};
